import clsx from "clsx";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { MemoryRouter, Route, Routes, useLocation } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import SoftSignupForm from "web/react/components/soft-signup-dialog/soft-signup-form";
import { ValuePropositionAnimation } from "web/react/components/value-proposition-animation";
import { View } from "web/react/emo/view";
import { ForgotPasswordEmailSentScreen } from "web/react/pages/account/signup-or-login-page/forgot-password-email-sent-screen";
import { ForgotPasswordScreen } from "web/react/pages/account/signup-or-login-page/forgot-password-screen";
import { LoginEmailSentScreen } from "web/react/pages/account/signup-or-login-page/login-email-sent-screen";
import { LoginScreen } from "web/react/pages/account/signup-or-login-page/login-screen";
import { CaptureType, closeCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import styles from "./soft-signup-dialog.module.css";

interface SoftSignupDialogProps {
    captureType: CaptureType;
    blockType?: string;
    analyticsEventLabel?: string;
}

function SoftSignupRoutes(): JSX.Element {
    const [email, setEmail] = useState("");
    const location = useLocation();

    return (
        <TransitionGroup className={styles.flexRow}>
            <CSSTransition
                key={location.key}
                classNames={{
                    enter: styles.enter,
                    enterActive: styles.enterActive,
                    exit: styles.exit,
                    exitActive: styles.exitActive,
                }}
                timeout={{ enter: 1000, exit: 500 }}
                mountOnEnter
                unmountOnExit
            >
                <View
                    className={styles.form}
                    marginTop={location.pathname === "/" ? {} : { lg: "xl" }}
                    padding={{ sm: "sm", lg: "lg" }}
                    paddingY={{ sm: "xl" }}
                >
                    <Routes location={location}>
                        <Route
                            index
                            element={<SoftSignupForm email={email} setEmail={setEmail} />}
                        />
                        {environment.getFeature("cw_new_wishlist_sign_up_flow_redesign") && (
                            <>
                                <Route
                                    path="/login"
                                    element={<LoginScreen email={email} headingStyle={"title-1"} />}
                                />
                                <Route
                                    path="/login/email-sent"
                                    element={
                                        <LoginEmailSentScreen
                                            email={email}
                                            headingStyle={"title-1"}
                                        />
                                    }
                                />
                                <Route
                                    path="/forgot-password"
                                    element={
                                        <ForgotPasswordScreen
                                            onSuccess={setEmail}
                                            email={email}
                                            headingStyle={"title-1"}
                                        />
                                    }
                                />
                                <Route
                                    path="/forgot-password/email-sent"
                                    element={
                                        <ForgotPasswordEmailSentScreen
                                            email={email}
                                            headingStyle={"title-1"}
                                        />
                                    }
                                />
                            </>
                        )}
                    </Routes>
                </View>
            </CSSTransition>
        </TransitionGroup>
    );
}

export const SoftSignupDialog = ({
    captureType,
    blockType,
    analyticsEventLabel,
}: SoftSignupDialogProps): JSX.Element => {
    const dispatch = useDispatch();
    function onAfterOpen(): void {
        analytics.event(captureType, "open_signup_form", "open_overlay");
        analytics.event("desktop_signup", "shown", analyticsEventLabel);
    }

    function onClose(): void {
        analytics.event(captureType, "close_signup_form", "exit_signup");
        dispatch(closeCustomerCaptureOverlay());
    }

    return (
        <GenericModal
            isOpen={true}
            className={styles.modal}
            closeClassName={styles.closeButton}
            onClose={onClose}
            closable={blockType !== "hard_block_after_action"}
            onAfterOpen={onAfterOpen}
        >
            <div
                className={clsx(styles.flex, {
                    [styles.bigHeight]: environment.getFeature(
                        "cw_new_wishlist_sign_up_flow_redesign"
                    ),
                })}
            >
                <View className={styles.formWrapper}>
                    <MemoryRouter>
                        <SoftSignupRoutes />
                    </MemoryRouter>
                </View>
                <View className={styles.propositionWrapper}>
                    <ValuePropositionAnimation className={styles.propositionAnimation} />
                </View>
            </div>
        </GenericModal>
    );
};
